// Pure colormap math + rendering-policy helpers. No THREE, no DOM.
//
// strengthToRGBA / colorOptsFromViewerCtx / strengthToColor live here even
// though they encode rendering policy (transparent-bounds, alpha=150) rather
// than pure colormap math — keeping them next to sampleColormap avoids a
// trivial wrapper module and lets the per-pixel slice rasteriser reach for
// one import.

import type {
  ColorRGB,
  ColorRGBA,
  ColormapBounds,
  ColormapDefs,
  ScalarField,
  StrengthToColorOpts,
} from './types';

// Minimal viewer/params surface the colormap helpers need. Avoids importing
// the full app/context type from a leaf module; tightened in PR 6/8.
interface ViewerColormapSlice {
  colormap: { defs: ColormapDefs };
}
interface ParamsColormapSlice {
  colormap: string;
  colormapMin: number;
  colormapMax: number;
  transparentBounds?: boolean;
  colormapLog?: boolean;
}

/** Sign-preserving log compressor used by the log-scale colormap branch.
 *  Acts as identity near 0 and as ±log10(|v|) for |v| >> 1 — equivalent to
 *  matplotlib's symlog with linthresh=1. */
export function symlog(v: number): number {
  return Math.sign(v) * Math.log10(1 + Math.abs(v));
}
/** Inverse of {@link symlog}. */
export function symlogInv(s: number): number {
  return Math.sign(s) * (Math.pow(10, Math.abs(s)) - 1);
}

export function sampleColormap(defs: ColormapDefs | null | undefined, displayName: string, t01: number): ColorRGB {
  const t = Math.max(0, Math.min(1, t01));
  const def = defs?.[displayName];
  if (def && def.length > 0) {
    let prev = def[0]!;
    for (let i = 1; i < def.length; i++) {
      const next = def[i]!;
      const p0 = Math.max(0, Math.min(1, +prev.pos));
      const p1 = Math.max(0, Math.min(1, +next.pos));
      if (t <= p1) {
        const u = p1 > p0 ? (t - p0) / (p1 - p0) : 0;
        return {
          r: Math.round(prev.r + (next.r - prev.r) * u),
          g: Math.round(prev.g + (next.g - prev.g) * u),
          b: Math.round(prev.b + (next.b - prev.b) * u),
        };
      }
      prev = next;
    }
    const last = def[def.length - 1]!;
    return { r: last.r, g: last.g, b: last.b };
  }
  return builtinRainbow(t);
}

export function builtinRainbow(t: number): ColorRGB {
  if (t < 0.25) { const u = t * 4; return { r: 0, g: 0, b: Math.floor(100 + u * 155) }; }
  if (t < 0.5)  { const u = (t - 0.25) * 4; return { r: 0, g: Math.floor(u * 255), b: 255 }; }
  if (t < 0.75) { const u = (t - 0.5) * 4; return { r: Math.floor(u * 255), g: 255, b: Math.floor((1 - u) * 255) }; }
  const u = (t - 0.75) * 4;
  return { r: 255, g: Math.floor((1 - u) * 255), b: 0 };
}

// Slider bounds + auto-range computation. Pure given fieldData.
export function calculateColormapBounds(fieldData: ScalarField): ColormapBounds {
  let absMin = Infinity, absMax = -Infinity;
  for (let i = 0; i < fieldData.length; i++) {
    const v = fieldData[i]!;
    if (v < absMin) absMin = v;
    if (v > absMax) absMax = v;
  }
  const hasNegativeValues = absMin < 0;
  const hasPositiveValues = absMax > 0;
  const hasBothSigns = hasNegativeValues && hasPositiveValues;

  const n = fieldData.length;
  let sum = 0, sumSq = 0;
  for (let i = 0; i < n; i++) {
    const v = fieldData[i]!;
    sum += v; sumSq += v * v;
  }
  const mean = sum / n;
  const variance = (sumSq / n) - (mean * mean);
  const stdDev = Math.sqrt(Math.max(variance, 0));

  const sorted = new Float32Array(fieldData);
  sorted.sort();
  const median = sorted[Math.floor(n / 2)]!;

  return { absMin, absMax, hasBothSigns, mean, stdDev, median };
}

export function strengthToRGBA(strength: number, opts: StrengthToColorOpts): ColorRGBA {
  const { min, max, defs, name, transparentBounds, log } = opts;
  if (transparentBounds && (strength < min || strength > max)) {
    return { r: 0, g: 0, b: 0, a: 0 };
  }
  let t: number;
  if (log) {
    const sMin = symlog(min), sMax = symlog(max);
    const sSpan = sMax - sMin || 1;
    t = (symlog(strength) - sMin) / sSpan;
  } else {
    const span = max - min || 1;
    t = (strength - min) / span;
  }
  t = Math.max(0, Math.min(1, t));
  const c = sampleColormap(defs, name, t);
  return { r: c.r, g: c.g, b: c.b, a: 150 };
}

export function colorOptsFromViewerCtx(viewer: ViewerColormapSlice, params: ParamsColormapSlice): StrengthToColorOpts {
  return {
    min: params.colormapMin,
    max: params.colormapMax,
    defs: viewer.colormap.defs,
    name: params.colormap,
    transparentBounds: !!params.transparentBounds,
    log: !!params.colormapLog,
  };
}

// Minimal binding-shaped slice used by the post-Stage-2 render path. Lives
// here as a structural type to avoid leaking the full ColormapBinding (and
// the @store dependency) into @physics.
interface BindingColormapSlice {
  colormap: string;
  min: number;
  max: number;
  log: boolean;
  transparentBounds: boolean;
}

export function colorOptsFromBinding(viewer: ViewerColormapSlice, binding: BindingColormapSlice): StrengthToColorOpts {
  return {
    min: binding.min,
    max: binding.max,
    defs: viewer.colormap.defs,
    name: binding.colormap,
    transparentBounds: !!binding.transparentBounds,
    log: !!binding.log,
  };
}

export function strengthToColor(viewer: ViewerColormapSlice, params: ParamsColormapSlice, strength: number): ColorRGBA {
  return strengthToRGBA(strength, colorOptsFromViewerCtx(viewer, params));
}

export function strengthToColorFromBinding(viewer: ViewerColormapSlice, binding: BindingColormapSlice, strength: number): ColorRGBA {
  return strengthToRGBA(strength, colorOptsFromBinding(viewer, binding));
}
